import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, Dropdown, IDropdownOption, IconButton, Pivot, PivotItem, Stack, Text } from "@fluentui/react";
import { ServerDataGrid, ServerDataGridColumn } from "@/ui-controls/ServerDataGrid";
import MoreActionsButton from "@/ui-controls/MoreActionsButton";
import SearchBox from "@/ui-controls/SearchBox";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import RigthPanel, { RigthPanelType } from "@/ui-controls/RigthPanel";
import OcrFeedbackDetailsPanel from "@/components/integrations/feedback/OcrFeedbackDetailsPanel";
import {
  DocumentClassificationFeedbackDto,
  NlqGenerationFeedbackDto,
  listOcrFeedback,
  listNlpFeedback,
  reviewOcrFeedback,
} from "@/api/feedback";
import {
  BooleanOperator,
  Comparison,
  OrderByDirection,
  PageRequest,
  PropertyFilter,
} from "@/api/types";

const ocrProviderOptions: IDropdownOption[] = [
  { key: "", text: "All Providers" },
  { key: "Peruse", text: "Peruse" },
  { key: "Tensorlake", text: "Tensorlake" },
  { key: "CloneOps", text: "CloneOps" },
];

const formatDate = (iso: string) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
};

const IntegrationFeedbackPage: React.FC = () => {
  // ── OCR state ──
  const [ocrFeedback, setOcrFeedback] = useState<DocumentClassificationFeedbackDto[]>([]);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrSearch, setOcrSearch] = useState("");
  const [showReviewed, setShowReviewed] = useState(false);
  const [ocrCustomerId, setOcrCustomerId] = useState<number | undefined>();
  const [ocrProvider, setOcrProvider] = useState<string>("");
  const [selectedOcrFeedback, setSelectedOcrFeedback] = useState<DocumentClassificationFeedbackDto | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // ── OCR pagination ──
  const [ocrPage, setOcrPage] = useState(1);
  const [ocrPageSize, setOcrPageSize] = useState(10);
  const [ocrTotalCount, setOcrTotalCount] = useState(0);
  const ocrTotalPages = Math.ceil(ocrTotalCount / ocrPageSize) || 1;

  // ── OCR sort state ──
  const [ocrSortKey, setOcrSortKey] = useState<"Id" | "CreatedAtUtc">("CreatedAtUtc");
  const [ocrSortDesc, setOcrSortDesc] = useState(true);

  // ── NLP state ──
  const [nlpFeedback, setNlpFeedback] = useState<NlqGenerationFeedbackDto[]>([]);
  const [nlpLoading, setNlpLoading] = useState(false);
  const [nlpSearch, setNlpSearch] = useState("");

  // ── NLP pagination ──
  const [nlpPage, setNlpPage] = useState(1);
  const [nlpPageSize, setNlpPageSize] = useState(10);
  const [nlpTotalCount, setNlpTotalCount] = useState(0);
  const nlpTotalPages = Math.ceil(nlpTotalCount / nlpPageSize) || 1;

  // ── NLP sort state ──
  const [nlpSortKey, setNlpSortKey] = useState<"Id" | "CreatedAtUtc">("CreatedAtUtc");
  const [nlpSortDesc, setNlpSortDesc] = useState(true);

  const buildOcrRequest = useCallback((): PageRequest => {
    const filters: PropertyFilter[] = [];

    if (!showReviewed) {
      filters.push({ field: "IsActive", comparison: Comparison.Equals, value: "true" });
    }
    if (ocrCustomerId !== undefined) {
      filters.push({ field: "CustomerId", comparison: Comparison.Equals, value: ocrCustomerId.toString() });
    }
    if (ocrProvider) {
      filters.push({ field: "Provider", comparison: Comparison.Equals, value: ocrProvider });
    }
    if (ocrSearch) {
      filters.push({ field: "ClassificationJobId", comparison: Comparison.Contains, value: ocrSearch });
    }

    return {
      pageNumber: ocrPage,
      pageSize: ocrPageSize,
      orderBy: { field: ocrSortKey, direction: ocrSortDesc ? OrderByDirection.Desc : OrderByDirection.Asc },
      ...(filters.length > 0 && {
        filterSet: {
          operator: filters.length > 1 ? BooleanOperator.And : BooleanOperator.None,
          filters,
        },
      }),
    };
  }, [showReviewed, ocrCustomerId, ocrProvider, ocrSearch, ocrPage, ocrPageSize, ocrSortKey, ocrSortDesc]);

  const buildNlpRequest = useCallback((): PageRequest => {
    const filters: PropertyFilter[] = [];

    if (nlpSearch) {
      filters.push({ field: "NaturalInput", comparison: Comparison.Contains, value: nlpSearch });
    }

    return {
      pageNumber: nlpPage,
      pageSize: nlpPageSize,
      orderBy: { field: nlpSortKey, direction: nlpSortDesc ? OrderByDirection.Desc : OrderByDirection.Asc },
      ...(filters.length > 0 && {
        filterSet: {
          operator: BooleanOperator.None,
          filters,
        },
      }),
    };
  }, [nlpSearch, nlpPage, nlpPageSize, nlpSortKey, nlpSortDesc]);

  const loadOcrFeedback = useCallback(() => {
    setOcrLoading(true);
    listOcrFeedback(buildOcrRequest())
      .then((data) => {
        setOcrFeedback(data?.result ?? []);
        setOcrTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => {
        setOcrFeedback([]);
        setOcrTotalCount(0);
      })
      .finally(() => setOcrLoading(false));
  }, [buildOcrRequest]);

  const loadNlpFeedback = useCallback(() => {
    setNlpLoading(true);
    listNlpFeedback(buildNlpRequest())
      .then((data) => {
        setNlpFeedback(data?.result ?? []);
        setNlpTotalCount(data?.totalCount ?? 0);
      })
      .catch(() => {
        setNlpFeedback([]);
        setNlpTotalCount(0);
      })
      .finally(() => setNlpLoading(false));
  }, [buildNlpRequest]);

  useEffect(() => {
    loadOcrFeedback();
  }, [loadOcrFeedback]);

  useEffect(() => {
    loadNlpFeedback();
  }, [loadNlpFeedback]);

  const handleReview = async (item: DocumentClassificationFeedbackDto) => {
    try {
      await reviewOcrFeedback(item.id);
      setIsDetailOpen(false);
      setSelectedOcrFeedback(null);
      loadOcrFeedback();
    } catch {
      // error handling could be improved
    }
  };

  const handleOcrSort = (key: "Id" | "CreatedAtUtc") => {
    if (ocrSortKey === key) {
      setOcrSortDesc((prev) => !prev);
    } else {
      setOcrSortKey(key);
      setOcrSortDesc(true);
    }
    setOcrPage(1);
  };

  const handleNlpSort = (key: "Id" | "CreatedAtUtc") => {
    if (nlpSortKey === key) {
      setNlpSortDesc((prev) => !prev);
    } else {
      setNlpSortKey(key);
      setNlpSortDesc(true);
    }
    setNlpPage(1);
  };

  // ── OCR columns ──
  const ocrColumns: ServerDataGridColumn[] = [
    {
      key: "id",
      name: "ID",
      fieldName: "id",
      minWidth: 60,
      maxWidth: 80,
      isSorted: ocrSortKey === "Id",
      isSortedDescending: ocrSortKey === "Id" ? ocrSortDesc : undefined,
      onColumnClick: () => handleOcrSort("Id"),
    },
    {
      key: "customerId",
      name: "Customer",
      fieldName: "customerId",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <Text>{item.customerId ?? "—"}</Text>
      ),
    },
    {
      key: "provider",
      name: "Provider",
      fieldName: "provider",
      minWidth: 80,
      maxWidth: 120,
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <Text>{item.provider || "—"}</Text>
      ),
    },
    {
      key: "classificationJobId",
      name: "Job ID",
      fieldName: "classificationJobId",
      minWidth: 120,
      maxWidth: 200,
    },
    {
      key: "success",
      name: "Success",
      fieldName: "success",
      minWidth: 80,
      maxWidth: 100,
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <Text
          styles={{
            root: {
              color: item.success ? "#107c10" : "#d13438",
              fontWeight: 600,
            },
          }}
        >
          {item.success ? "Yes" : "No"}
        </Text>
      ),
    },
    {
      key: "error",
      name: "Error",
      fieldName: "error",
      minWidth: 150,
      maxWidth: 300,
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <Text styles={{ root: { color: item.error ? "#d13438" : "#a19f9d" } }}>
          {item.error || "—"}
        </Text>
      ),
    },
    ...(showReviewed
      ? [
          {
            key: "isActive",
            name: "Status",
            fieldName: "isActive",
            minWidth: 80,
            maxWidth: 100,
            onRender: (item: DocumentClassificationFeedbackDto) => (
              <Text
                styles={{
                  root: {
                    color: item.isActive ? "#107c10" : "#a19f9d",
                    fontWeight: 600,
                  },
                }}
              >
                {item.isActive ? "Pending" : "Reviewed"}
              </Text>
            ),
          } as ServerDataGridColumn,
        ]
      : []),
    {
      key: "createdAtUtc",
      name: "Created",
      fieldName: "createdAtUtc",
      minWidth: 150,
      maxWidth: 200,
      isSorted: ocrSortKey === "CreatedAtUtc",
      isSortedDescending: ocrSortKey === "CreatedAtUtc" ? ocrSortDesc : undefined,
      onColumnClick: () => handleOcrSort("CreatedAtUtc"),
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <Text>{formatDate(item.createdAtUtc)}</Text>
      ),
    },
    {
      key: "actions",
      name: "",
      minWidth: 50,
      maxWidth: 70,
      onRender: (item: DocumentClassificationFeedbackDto) => (
        <MoreActionsButton
          menu={[
            {
              key: "details",
              text: "View feedback",
              iconProps: { iconName: "View" },
              onClick: () => {
                setSelectedOcrFeedback(item);
                setIsDetailOpen(true);
              },
            },
            ...(item.isActive
              ? [
                  {
                    key: "review",
                    text: "Mark as reviewed",
                    iconProps: { iconName: "CheckMark" },
                    onClick: () => handleReview(item),
                  },
                ]
              : []),
          ]}
        />
      ),
    },
  ];

  // ── NLP columns ──
  const nlpColumns: ServerDataGridColumn[] = [
    {
      key: "id",
      name: "ID",
      fieldName: "id",
      minWidth: 60,
      maxWidth: 80,
      isSorted: nlpSortKey === "Id",
      isSortedDescending: nlpSortKey === "Id" ? nlpSortDesc : undefined,
      onColumnClick: () => handleNlpSort("Id"),
    },
    {
      key: "customerId",
      name: "Customer",
      fieldName: "customerId",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "feedbackType",
      name: "Type",
      fieldName: "feedbackType",
      minWidth: 80,
      maxWidth: 120,
    },
    {
      key: "instanceName",
      name: "Instance",
      fieldName: "instanceName",
      minWidth: 100,
      maxWidth: 150,
    },
    {
      key: "naturalInput",
      name: "Input",
      fieldName: "naturalInput",
      minWidth: 200,
      maxWidth: 400,
      onRender: (item: NlqGenerationFeedbackDto) => (
        <Text
          styles={{ root: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }}
          title={item.naturalInput}
        >
          {item.naturalInput}
        </Text>
      ),
    },
    {
      key: "isValid",
      name: "Valid",
      fieldName: "isValid",
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: NlqGenerationFeedbackDto) => (
        <Text
          styles={{
            root: {
              color: item.isValid ? "#107c10" : "#d13438",
              fontWeight: 600,
            },
          }}
        >
          {item.isValid ? "Yes" : "No"}
        </Text>
      ),
    },
    {
      key: "llmModel",
      name: "Model",
      fieldName: "llmModel",
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: NlqGenerationFeedbackDto) => (
        <Text>{item.llmModel || "—"}</Text>
      ),
    },
    {
      key: "userFeedbackScore",
      name: "Score",
      fieldName: "userFeedbackScore",
      minWidth: 60,
      maxWidth: 80,
      onRender: (item: NlqGenerationFeedbackDto) => (
        <Text>{item.userFeedbackScore ?? "—"}</Text>
      ),
    },
    {
      key: "createdAtUtc",
      name: "Created",
      fieldName: "createdAtUtc",
      minWidth: 150,
      maxWidth: 200,
      isSorted: nlpSortKey === "CreatedAtUtc",
      isSortedDescending: nlpSortKey === "CreatedAtUtc" ? nlpSortDesc : undefined,
      onColumnClick: () => handleNlpSort("CreatedAtUtc"),
      onRender: (item: NlqGenerationFeedbackDto) => (
        <Text>{formatDate(item.createdAtUtc)}</Text>
      ),
    },
  ];

  return (
    <Stack tokens={{ childrenGap: 16 }} styles={{ root: { padding: "24px 0" } }}>
      <Text variant="xLarge" styles={{ root: { fontWeight: 600 } }}>
        Feedback
      </Text>
      <Text variant="medium" styles={{ root: { color: "#605e5c" } }}>
        View feedback records from OCR document classification and NLP query generation.
      </Text>

      <Pivot>
        {/* ── OCR Feedback Tab ── */}
        <PivotItem headerText="OCR Feedback">
          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { paddingTop: 16 } }}>
            <Stack horizontal tokens={{ childrenGap: 16 }} verticalAlign="center"
              styles={{ root: { "& .search-box": { padding: 0 } } as any }}>
              <CustomerComboBox
                label=""
                selectedCustomerId={ocrCustomerId}
                onChange={(id) => { setOcrCustomerId(id); setOcrPage(1); }}
                placeholder="Filter by customer..."
              />
              <Dropdown
                options={ocrProviderOptions}
                selectedKey={ocrProvider}
                onChange={(_, option) => { setOcrProvider((option?.key as string) ?? ""); setOcrPage(1); }}
                styles={{ dropdown: { minWidth: 150 } }}
              />
              <SearchBox
                placeholder="Search by Job ID..."
                onSearch={(val) => { setOcrSearch(val); setOcrPage(1); }}
              />
              <Checkbox
                label="Show reviewed"
                checked={showReviewed}
                onChange={(_, checked) => { setShowReviewed(!!checked); setOcrPage(1); }}
              />
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={loadOcrFeedback}
                disabled={ocrLoading}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: {
                    fontSize: 18,
                    color: ocrLoading ? "#a19f9d" : "#0078d4",
                  },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                  rootDisabled: { backgroundColor: "transparent" },
                }}
              />
            </Stack>
            <ServerDataGrid
              items={ocrFeedback}
              columns={ocrColumns}
              loading={ocrLoading}
              pageSize={ocrPageSize}
              currentPage={ocrPage}
              totalPages={ocrTotalPages}
              totalCount={ocrTotalCount}
              onPageChange={setOcrPage}
              onPageSizeChange={(size) => { setOcrPageSize(size); setOcrPage(1); }}
            />
          </Stack>
        </PivotItem>

        {/* ── NLP Feedback Tab ── */}
        <PivotItem headerText="NLP Feedback">
          <Stack tokens={{ childrenGap: 12 }} styles={{ root: { paddingTop: 16 } }}>
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <SearchBox
                placeholder="Search by input..."
                onSearch={(val) => { setNlpSearch(val); setNlpPage(1); }}
              />
              <IconButton
                iconProps={{ iconName: "Refresh" }}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={loadNlpFeedback}
                disabled={nlpLoading}
                styles={{
                  root: { width: 40, height: 40 },
                  rootHovered: { backgroundColor: "#f3f2f1" },
                  icon: {
                    fontSize: 18,
                    color: nlpLoading ? "#a19f9d" : "#0078d4",
                  },
                  rootPressed: { backgroundColor: "#e1dfdd" },
                  rootDisabled: { backgroundColor: "transparent" },
                }}
              />
            </Stack>
            <ServerDataGrid
              items={nlpFeedback}
              columns={nlpColumns}
              loading={nlpLoading}
              pageSize={nlpPageSize}
              currentPage={nlpPage}
              totalPages={nlpTotalPages}
              totalCount={nlpTotalCount}
              onPageChange={setNlpPage}
              onPageSizeChange={(size) => { setNlpPageSize(size); setNlpPage(1); }}
            />
          </Stack>
        </PivotItem>
      </Pivot>

      {/* ── OCR Feedback Detail Panel ── */}
      {selectedOcrFeedback && (
        <RigthPanel
          header="Feedback Details"
          isOpen={isDetailOpen}
          type={RigthPanelType.medium}
          dismissPanel={() => {
            setIsDetailOpen(false);
            setSelectedOcrFeedback(null);
          }}
          childComponent={OcrFeedbackDetailsPanel}
          childProps={{
            feedback: selectedOcrFeedback,
            onReview: () => handleReview(selectedOcrFeedback),
            onClose: () => {
              setIsDetailOpen(false);
              setSelectedOcrFeedback(null);
            },
          }}
        />
      )}
    </Stack>
  );
};

export default IntegrationFeedbackPage;
